import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
    User, Bell, LogOut, Clock, Trash2, Loader2, Heart, 
    MessageCircle, Send, Trophy, Star, TrendingUp
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { DealCard } from '../components/DealCard';
import { toast } from 'sonner';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import NotificationPreferences from '../components/NotificationPreferences';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { API } from '../config/apiBase';


export default function ProfilePage() {
    const { user, logout, isApproved } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [profile, setProfile] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [myDeals, setMyDeals] = useState([]);
    const [myComments, setMyComments] = useState([]);
    const [wishlist, setWishlist] = useState({ wishlist: [], deals: [] });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        setActiveTab(searchParams.get('tab') || 'profile');
        fetchData();
    }, [user, navigate, searchParams]);

    const fetchData = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        try {
            const [profileRes, alertsRes, dealsRes, commentsRes, wishlistRes] = await Promise.all([
                fetch(`${API}/users/${user?.user_id}/profile`),
                fetch(`${API}/alerts`, { credentials: 'include', headers }),
                fetch(`${API}/users/${user?.user_id}/deals`),
                fetch(`${API}/users/${user?.user_id}/comments`),
                fetch(`${API}/wishlist`, { credentials: 'include', headers })
            ]);
            
            if (profileRes.ok) setProfile(await profileRes.json());
            if (alertsRes.ok) setAlerts(await alertsRes.json());
            if (dealsRes.ok) {
                const data = await dealsRes.json();
                setMyDeals(data.deals || []);
            }
            if (commentsRes.ok) setMyComments(await commentsRes.json());
            if (wishlistRes.ok) setWishlist(await wishlistRes.json());
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAlert = async (alertId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API}/alerts/${alertId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            if (response.ok) {
                setAlerts(alerts.filter(a => a.alert_id !== alertId));
                toast.success('Alerta removido');
            }
        } catch (error) {
            toast.error('Erro ao remover alerta');
        }
    };

    const handleRemoveFromWishlist = async (dealId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API}/wishlist/${dealId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            if (response.ok) {
                setWishlist(prev => ({
                    wishlist: prev.wishlist.filter(w => w.deal_id !== dealId),
                    deals: prev.deals.filter(d => d.deal_id !== dealId)
                }));
                toast.success('Removido da lista de desejos');
            }
        } catch (error) {
            toast.error('Erro ao remover');
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
        toast.success('Até logo!');
    };

    if (!user) return null;

    const displayProfile = profile || user;

    return (
        <div className="min-h-screen bg-background theme-transition" data-testid="profile-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Profile Header */}
                <div className="bg-card rounded-xl border border-border p-4 sm:p-6 mb-8" data-testid="profile-card">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                        {/* Avatar */}
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-muted border-4 border-gold-500/30 overflow-hidden flex-shrink-0">
                            {displayProfile.picture ? (
                                <img src={resolveImageUrl(displayProfile.picture)} alt={displayProfile.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-2xl sm:text-3xl text-gold-500 font-bold">
                                    {displayProfile.name?.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0 w-full text-center sm:text-left">
                            <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-2">
                                <div>
                                    <h1 className="text-xl sm:text-2xl font-outfit font-bold text-foreground">{displayProfile.name}</h1>
                                    <p className="text-blue-400 text-sm font-medium mt-1">
                                        Nível {displayProfile.level || 1} - {displayProfile.level_name || 'Novato'}
                                    </p>
                                </div>
                                <Button
                                    onClick={handleLogout}
                                    variant="outline"
                                    size="sm"
                                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                    data-testid="logout-btn"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Sair
                                </Button>
                            </div>
                            
                            {/* Level Progress */}
                            <div className="mt-4">
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                                    <span className="flex items-center gap-1">
                                        <TrendingUp className="w-3.5 h-3.5" />
                                        Próximo nível
                                    </span>
                                    <span>{displayProfile.points || 0} / {displayProfile.next_level_threshold || 100}</span>
                                </div>
                                <div className="progress-bar h-2 rounded-full">
                                    <div 
                                        className="progress-fill rounded-full" 
                                        style={{ width: `${Math.min(((displayProfile.points || 0) / (displayProfile.next_level_threshold || 100)) * 100, 100)}%` }}
                                    />
                                </div>
                            </div>
                            
                            {/* Stats - grid for mobile */}
                            <div className="grid grid-cols-2 sm:flex sm:items-center gap-3 sm:gap-6 mt-4">
                                <div className="flex items-center gap-1.5">
                                    <Trophy className="w-3.5 h-3.5 text-gold-500 flex-shrink-0" />
                                    <span className="text-xs text-muted-foreground truncate">
                                        <strong className="text-foreground">{displayProfile.points || 0}</strong> pts
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Send className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                                    <span className="text-xs text-muted-foreground truncate">
                                        <strong className="text-foreground">{displayProfile.deals_count || 0}</strong> ofertas
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <MessageCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                    <span className="text-xs text-muted-foreground truncate">
                                        <strong className="text-foreground">{displayProfile.comments_count || 0}</strong> coments
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Star className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                                    <span className="text-xs text-muted-foreground truncate">
                                        <strong className="text-foreground">{displayProfile.likes_received || 0}</strong> likes
                                    </span>
                                </div>
                            </div>
                            
                            {/* Role Badge */}
                            <div className="mt-4">
                                <span className={`text-xs px-3 py-1 rounded-full ${
                                    displayProfile.role === 'admin' 
                                        ? 'bg-red-500/20 text-red-400' 
                                        : isApproved 
                                            ? 'bg-green-500/20 text-green-400'
                                            : 'bg-muted text-muted-foreground'
                                }`}>
                                    {displayProfile.role === 'admin' ? 'Administrador' : isApproved ? 'Usuário Aprovado' : 'Usuário'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs - Promobit Style */}
                <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={`tab-pill flex items-center gap-2 whitespace-nowrap ${activeTab === 'profile' ? 'active' : ''}`}
                        data-testid="tab-profile"
                    >
                        <User className="w-4 h-4" />
                        Perfil
                    </button>
                    <button
                        onClick={() => setActiveTab('deals')}
                        className={`tab-pill flex items-center gap-2 whitespace-nowrap ${activeTab === 'deals' ? 'active' : ''}`}
                        data-testid="tab-my-deals"
                    >
                        <Send className="w-4 h-4" />
                        Minhas Ofertas
                    </button>
                    <button
                        onClick={() => setActiveTab('comments')}
                        className={`tab-pill flex items-center gap-2 whitespace-nowrap ${activeTab === 'comments' ? 'active' : ''}`}
                        data-testid="tab-comments"
                    >
                        <MessageCircle className="w-4 h-4" />
                        Comentários
                    </button>
                    <button
                        onClick={() => setActiveTab('wishlist')}
                        className={`tab-pill flex items-center gap-2 whitespace-nowrap ${activeTab === 'wishlist' ? 'active' : ''}`}
                        data-testid="tab-wishlist"
                    >
                        <Heart className="w-4 h-4" />
                        Lista de Desejos
                    </button>
                    <button
                        onClick={() => setActiveTab('alerts')}
                        className={`tab-pill flex items-center gap-2 whitespace-nowrap ${activeTab === 'alerts' ? 'active' : ''}`}
                        data-testid="tab-alerts"
                    >
                        <Bell className="w-4 h-4" />
                        Alertas de Preço
                    </button>
                    <button
                        onClick={() => setActiveTab('notificacoes')}
                        className={`tab-pill flex items-center gap-2 whitespace-nowrap ${activeTab === 'notificacoes' ? 'active' : ''}`}
                        data-testid="tab-notificacoes"
                    >
                        <Bell className="w-4 h-4" />
                        Notificações
                    </button>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Profile Tab */}
                        {activeTab === 'profile' && (
                            <div className="bg-card rounded-xl border border-border p-6">
                                <h3 className="text-lg font-semibold text-foreground mb-4">Informações da Conta</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs text-muted-foreground uppercase tracking-wider">Email</label>
                                        <p className="text-foreground">{displayProfile.email}</p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-muted-foreground uppercase tracking-wider">Membro desde</label>
                                        <p className="text-foreground">
                                            {displayProfile.created_at ? new Date(displayProfile.created_at).toLocaleDateString('pt-BR', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            }) : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Deals Tab */}
                        {activeTab === 'deals' && (
                            <div>
                                {myDeals.length === 0 ? (
                                    <div className="text-center py-16 bg-card rounded-xl border border-border">
                                        <Send className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                        <p className="text-muted-foreground">Você ainda não publicou nenhuma oferta</p>
                                        {isApproved && (
                                            <Link to="/criar-oferta">
                                                <Button className="btn-gold mt-4">
                                                    Publicar Primeira Oferta
                                                </Button>
                                            </Link>
                                        )}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {myDeals.map((deal) => (
                                            <DealCard key={deal.deal_id} deal={deal} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Comments Tab */}
                        {activeTab === 'comments' && (
                            <div className="space-y-4">
                                {myComments.length === 0 ? (
                                    <div className="text-center py-16 bg-card rounded-xl border border-border">
                                        <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                        <p className="text-muted-foreground">Você ainda não fez nenhum comentário</p>
                                    </div>
                                ) : (
                                    myComments.map((comment) => (
                                        <div key={comment.comment_id} className="bg-card rounded-xl border border-border p-4">
                                            {comment.deal && (
                                                <Link to={`/oferta/${comment.deal_id}`} className="flex items-center gap-3 mb-3 hover:opacity-80">
                                                    <img 
                                                        src={comment.deal.image_url} 
                                                        alt={comment.deal.title}
                                                        className="w-12 h-12 object-contain bg-white rounded"
                                                    />
                                                    <span className="text-sm font-medium text-foreground truncate">{comment.deal.title}</span>
                                                </Link>
                                            )}
                                            <p className="text-muted-foreground">{comment.content}</p>
                                            <p className="text-xs text-muted-foreground mt-2">
                                                {formatDistanceToNow(new Date(comment.created_at), { locale: ptBR, addSuffix: true })}
                                            </p>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Wishlist Tab */}
                        {activeTab === 'wishlist' && (
                            <div>
                                {wishlist.deals.length === 0 ? (
                                    <div className="text-center py-16 bg-card rounded-xl border border-border">
                                        <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                        <p className="text-muted-foreground">Sua lista de desejos está vazia</p>
                                        <Link to="/">
                                            <Button variant="outline" className="mt-4 border-gold-500/50 text-gold-500">
                                                Explorar Ofertas
                                            </Button>
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {wishlist.deals.map((deal) => (
                                            <div key={deal.deal_id} className="relative">
                                                <DealCard deal={deal} />
                                                <button
                                                    onClick={() => handleRemoveFromWishlist(deal.deal_id)}
                                                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-red-500/90 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                                                    title="Remover da lista"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Alerts Tab */}
                        {activeTab === 'alerts' && (
                            <div className="space-y-4">
                                {alerts.length === 0 ? (
                                    <div className="text-center py-16 bg-card rounded-xl border border-border">
                                        <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                        <p className="text-muted-foreground mb-2">Nenhum alerta de preço configurado</p>
                                        <p className="text-xs text-muted-foreground">Crie alertas nas páginas de ofertas para ser notificado quando o preço baixar</p>
                                    </div>
                                ) : (
                                    alerts.map((alert) => (
                                        <div 
                                            key={alert.alert_id} 
                                            className="flex items-center justify-between bg-card rounded-xl border border-border p-4"
                                            data-testid={`alert-${alert.alert_id}`}
                                        >
                                            <div>
                                                <p className="text-sm text-muted-foreground">Oferta: {alert.deal_id}</p>
                                                <p className="text-foreground font-medium">
                                                    Alerta para: R$ {alert.target_price.toFixed(2)}
                                                </p>
                                            </div>
                                            <Button
                                                onClick={() => handleDeleteAlert(alert.alert_id)}
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Notificações Tab */}
                        {activeTab === 'notificacoes' && (
                            <div className="bg-card rounded-xl border border-border p-4 sm:p-6">
                                <h3 className="text-lg font-semibold text-foreground mb-1">Notificações e categorias</h3>
                                <p className="text-xs text-muted-foreground mb-4">
                                    Escolha de quais categorias quer receber ofertas e ative as notificações push.
                                </p>
                                <NotificationPreferences token={localStorage.getItem('token')} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
